import React from 'react';
import PropTypes from 'prop-types';
import { LuAlertTriangle, LuCheck, LuFileX, LuLock, LuPencilLine } from 'react-icons/lu';
import { Button } from 'components/common';

const jobNumbers = (row) => (row.job_numbers?.length ? row.job_numbers.join(', ') : row.j_number);

const Progress = ({ row }) => {
  const done = row.buildings_count > 0 && row.checked_count >= row.buildings_count;
  return (
    <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-semibold ${done ? 'tw-text-emerald-700' : 'tw-text-slate-500'}`}>
      {done && <LuCheck size={12} />}
      {row.checked_count}/{row.buildings_count} checked
    </span>
  );
};
Progress.propTypes = { row: PropTypes.object.isRequired };

// A project with no contract yet: its check runs on the project until then.
const NoContractRow = ({ row, selected, onSelect }) => {
  const numbers = [row.q_number, ...(row.job_numbers || [])].filter(Boolean).join(' · ');
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(row)}
        aria-current={selected ? 'true' : undefined}
        className={`tw-w-full tw-text-left tw-rounded-lg tw-px-3 tw-py-2 tw-border tw-border-solid tw-transition-colors ${
          selected ? 'tw-bg-primary-50 tw-border-primary-200' : 'tw-bg-white tw-border-transparent hover:tw-bg-slate-50'}`}
      >
        <div className="tw-text-[13px] tw-font-semibold tw-text-slate-800 tw-truncate">{row.project_name}</div>
        {numbers && <div className="tw-text-xs tw-text-slate-500 tw-truncate">{numbers}</div>}
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-[11px] tw-text-slate-400">
          {row.stage && <span className="tw-truncate">{row.stage}</span>}
          {row.buildings_count > 0 && <span className="tw-ml-auto tw-shrink-0"><Progress row={row} /></span>}
          {row.has_proposal === false && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-amber-700 tw-shrink-0"><LuFileX size={11} /> Proposal missing</span>
          )}
        </div>
      </button>
    </li>
  );
};
NoContractRow.propTypes = { row: PropTypes.object.isRequired, selected: PropTypes.bool, onSelect: PropTypes.func.isRequired };

// Contracts on Commercial → PIF Check. Colour only for meaning: green = every
// building checked, amber = a check went stale or no proposal is uploaded yet.
const PifCheckContractList = ({ rows, total, selectedId, selectedProjectId, loading, busy = false, emptyText = 'No contracts match.', onSelect, onLoadMore }) => {
  if (!rows) return <p className="tw-px-2 tw-py-4 tw-text-xs tw-text-slate-400 tw-m-0">Loading…</p>;
  if (rows.length === 0) return <p className="tw-px-2 tw-py-4 tw-text-xs tw-text-slate-400 tw-m-0">{emptyText}</p>;

  return (
    <div className="tw-flex tw-flex-col">
      <ul aria-busy={busy} className={`tw-list-none tw-m-0 tw-p-0 tw-flex tw-flex-col tw-gap-1 tw-transition-opacity ${busy ? 'tw-opacity-50' : ''}`}>
        {rows.map((row) => {
          if (row.no_contract) {
            return <NoContractRow key={`project-${row.project_id}`} row={row} selected={row.project_id === selectedProjectId} onSelect={onSelect} />;
          }
          const selected = row.contract_id === selectedId;
          return (
            <li key={row.contract_id}>
              <button
                type="button"
                onClick={() => onSelect(row)}
                aria-current={selected ? 'true' : undefined}
                className={`tw-w-full tw-text-left tw-rounded-lg tw-px-3 tw-py-2 tw-border tw-border-solid tw-transition-colors ${
                  selected ? 'tw-bg-primary-50 tw-border-primary-200' : 'tw-bg-white tw-border-transparent hover:tw-bg-slate-50'}`}
              >
                <div className="tw-flex tw-items-center tw-gap-2">
                  <span className="tw-text-[13px] tw-font-semibold tw-text-slate-800 tw-truncate">{row.contract_number || `Contract #${row.contract_id}`}</span>
                  {row.released && <LuLock size={11} className="tw-text-slate-400 tw-shrink-0" title="Released" />}
                  <span className="tw-ml-auto tw-shrink-0"><Progress row={row} /></span>
                </div>
                <div className="tw-text-xs tw-text-slate-500 tw-truncate">
                  {/* Every job number of the contract: the project's and each building's (search matches any). */}
                  {row.project_name}{jobNumbers(row) ? ` · ${jobNumbers(row)}` : ''}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-[11px] tw-text-slate-400">
                  <span className="tw-truncate">{row.job_building_name}</span>
                  {/* Proposal = Sales' file, or its values typed by hand ("Type the Proposal values"). */}
                  {!row.has_proposal && (
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-amber-700"
                      title="Sales' Proposal not added yet: upload its file or type its values (step 2). Nothing to compare with the PIF until then.">
                      <LuFileX size={11} /> Proposal missing
                    </span>
                  )}
                  {row.has_proposal && row.proposal_kind === 'typed' && (
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-slate-500"
                      title="Proposal values typed by hand — no file uploaded">
                      <LuPencilLine size={11} /> Proposal typed
                    </span>
                  )}
                  {row.stale_count > 0 && (
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-amber-700"><LuAlertTriangle size={11} /> {row.stale_count} changed</span>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      {rows.length < total && (
        <Button type="button" variant="ghost" size="sm" className="tw-mt-2" onClick={onLoadMore} loading={loading}>
          Load more ({total - rows.length})
        </Button>
      )}
    </div>
  );
};

PifCheckContractList.propTypes = {
  rows:       PropTypes.array,
  total:      PropTypes.number,
  selectedId: PropTypes.number,
  selectedProjectId: PropTypes.number,
  loading:    PropTypes.bool,
  busy:       PropTypes.bool,
  emptyText:  PropTypes.string,
  onSelect:   PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
};

export default PifCheckContractList;
